import { parseSerieDescription } from './Schema';

/**
 * Which series of a study carries the radiology report, and under which Orthanc
 * attachment id.
 *
 * Single source for the viewer and the offline duplicator, which see series in
 * two different shapes — hence the two adapters below. They ask two different
 * questions, and holding both answers here is what keeps the writer writing where
 * the reader looks: a report written onto any other series is hidden from every
 * reader for good.
 *
 * The reader — findReportSeriesOfGroup — has to answer with one document, so it
 * resolves inside one group: the group of the series the viewer has on screen, which
 * the caller identifies. A group is the finest thing that designates a document
 * here. One study commonly carries DE, EN, FR and PT summaries at the very same
 * SeriesNumber, and after a reprocessing it carries two versions of one same
 * language; anything coarser than a group tells some of those apart and not the
 * others, and the losing side is a radiologist reading a document that does not
 * describe the images in front of them. Which group that is, screen included and the
 * one bounded case where the screen cannot say, is groupKeyToResolveIn.
 *
 * The writer — findReportSeriesPerGroup — has no series on screen to resolve
 * against: it writes the report of every group it downloads, each under the series
 * that carries it. Both go through the same grouping, the same ordering and the same
 * preference, so the file the writer fills is the file the reader opens.
 *
 * scripts/attach-reports.sh mirrors the ordering and the SUMMARY preference in
 * jq, but deliberately attaches onto EN|CE only. A narrower attach step than the
 * reader loses nothing: on a group whose language has no attachment the request
 * answers 404, which the cache already stores as "no report".
 */

/**
 * Orthanc reserves every content type below 1024, and the DICOMweb plugin
 * already occupies 4301 on these series. 9999 echoes the AZmed private DICOM tag
 * group (99991001, 99991002) — a different numbering space, but the same number
 * makes the origin obvious.
 *
 * Mirrored by REPORT_ATTACHMENT_ID in scripts/attach-reports.sh.
 */
export const REPORT_ATTACHMENT_ID = 9999;

/**
 * The stored shape of a report, shared by the browser cache and the offline
 * duplicator so that a disk hit and a Cache API hit are indistinguishable to the
 * reader, revalidation included.
 * @param {Object} fields
 * @param {string} fields.seriesInstanceUID The series the report hangs from
 * @param {string} fields.seriesOrthancId Its Orthanc id, to skip the next lookup
 * @param {Object|null} fields.report The report itself, null when there is none
 * @param {Object|null} fields.validator `{ kind: 'etag'|'md5', value }`
 * @returns {Object} The envelope
 */
export const buildReportEnvelope = ({
  seriesInstanceUID,
  seriesOrthancId,
  report,
  validator,
}) => ({
  seriesInstanceUID,
  seriesOrthancId,
  report: report || null,
  validator: validator || null,
});

/**
 * @param {Object} candidate A normalised series candidate
 * @returns {Object} Its parsed series description, null fields when absent
 */
const describe = candidate =>
  parseSerieDescription(candidate.SeriesDescription || '');

/**
 * Orders the candidates the way the jq of scripts/attach-reports.sh does.
 * Neither Orthanc nor the viewer guarantees an order, so "first" has to be
 * spelled out or the writer and the reader could pick different series.
 * @param {Object} a
 * @param {Object} b
 * @returns {number}
 */
const bySeriesNumberThenUID = (a, b) => {
  const numberA = Number(a.SeriesNumber) || 0;
  const numberB = Number(b.SeriesNumber) || 0;

  if (numberA !== numberB) return numberA - numberB;

  // Compared by code point rather than localeCompare: the script sorts in jq,
  // which knows nothing of the browser's locale.
  const uidA = String(a.SeriesInstanceUID);
  const uidB = String(b.SeriesInstanceUID);

  if (uidA === uidB) return 0;

  return uidA < uidB ? -1 : 1;
};

/**
 * Whether a candidate carries the AZmed series description at all, both halves of
 * the language pair required. The original acquisition series ("AP/PA - 1") carry no
 * schema, so they fall out here rather than needing a rule of their own — and they
 * have to, or they would all collapse into one group whose key is made of nulls.
 * @param {Object} candidate A normalised series candidate
 * @returns {boolean}
 */
const carriesSchema = candidate => {
  const { Langue, Certification } = describe(candidate);

  return Boolean(Langue && Certification);
};

/**
 * Which card of the study list a series belongs to — the five fields a card is
 * identified by, in the order the list itself spells them.
 *
 * All five, and not the language pair alone: after a reprocessing following a
 * version bump, one study carries two series of the same Langue and Certification
 * under different Algorithm, Version or Option. Those are two cards, two sets of
 * images and two reports, and the two must not be conflated.
 *
 * @param {Object} fields Parsed series description fields, Schema.js field names
 * @returns {string} The group key, `null` spelled out for the absent fields
 */
export const seriesGroupKey = ({
  Algorithm,
  Langue,
  Version,
  Certification,
  Option,
}) => `${Algorithm}|${Langue}|${Version}|${Certification}|${Option}`;

/**
 * The group a single candidate belongs to.
 *
 * The one and only answer to "which group is this series part of", so that the
 * reader resolving inside a group, the writer bucketing into groups and the study
 * list cutting its cards cannot end up disagreeing on where a series sits.
 *
 * @param {Object} [candidate] A normalised series candidate
 * @returns {string|null} Its group key, null when the series carries no schema and
 *   so belongs to no group at all, and null for no candidate
 */
const groupKeyOf = candidate =>
  candidate && carriesSchema(candidate)
    ? seriesGroupKey(describe(candidate))
    : null;

/**
 * Which of the ordered candidates carries the report: the SUMMARY, or failing
 * that the first DUPLICATA.
 * @param {Object[]} ordered Candidates sorted by bySeriesNumberThenUID
 * @returns {Object|undefined}
 */
const preferredOf = ordered =>
  ordered.find(c => describe(c).ImageType === 'SUMMARY') ||
  ordered.find(c => describe(c).ImageType === 'DUPLICATA');

/**
 * The series carrying the report of one group.
 *
 * For the reader, which resolves in the group of the series the viewer shows.
 * Nothing outside that group is ever answered with: another group is another
 * document — another language, or the previous version of these very images — and
 * handing it over would describe to a radiologist something other than what they are
 * looking at. No group, or a group whose series carry no report, is an absence, and
 * the caller reports it as one.
 *
 * @param {Object[]} candidates Normalised series, as built by the adapters
 * @param {string|null} [groupKey] The group to resolve in, from groupKeyToResolveIn
 * @returns {Object|undefined} The whole candidate, so callers can read orthancId
 */
export const findReportSeriesOfGroup = (candidates, groupKey) => {
  if (!groupKey) return undefined;

  const group = (candidates || []).filter(
    candidate => groupKeyOf(candidate) === groupKey
  );

  // Sorted right next to the pick it feeds: the order is what makes the reader, the
  // writer and the jq of the attach script land on the same series. Safe to sort in
  // place, the filter above returned a fresh array.
  return preferredOf(group.sort(bySeriesNumberThenUID));
};

/**
 * The distinct groups present among the candidates, in first-seen order. Series
 * carrying no schema have no group and are simply absent from it, see groupKeyOf.
 * @param {Object[]} candidates Normalised series candidates
 * @returns {Set<string>} The group keys
 */
const groupKeysOf = candidates =>
  new Set(candidates.map(groupKeyOf).filter(Boolean));

/**
 * Which group the report has to be resolved in, given what the viewer has on screen.
 *
 * The screen decides whenever it can say anything: the group of the displayed series.
 * It cannot when that series carries no schema — an original acquisition series such
 * as "AP/PA - 1", the one the viewer opens by default, display sets being ordered by
 * SeriesNumber and the acquisition coming before the series derived from it.
 *
 * The fallback is bounded to the single case where nothing is chosen: exactly one
 * group among the candidates, which is then the only report this study can be
 * describing, so answering it is a deduction and not a guess.
 *
 * Two groups and more is a refusal, spelled out rather than resolved. The two are
 * typically the 1.2 and the 1.3 of one reprocessed study, and there is nothing in an
 * acquisition series that says which of them the reader means — picking would put the
 * superseded document next to the images of the current version, in front of a
 * radiologist with no sign that it is the wrong one. An absence is at least honest,
 * and selecting an analysed series answers it immediately. Zero group is a refusal
 * for the plain reason that there is nothing to answer with.
 *
 * @param {Object[]} candidates Normalised series, as built by the adapters
 * @param {Object} [shown] The candidate on screen, as found by findShownCandidate
 * @returns {string|null} The group key to resolve in, null when there is none to
 *   settle on
 */
export const groupKeyToResolveIn = (candidates, shown) => {
  const shownKey = groupKeyOf(shown);

  if (shownKey) return shownKey;

  const keys = groupKeysOf(candidates || []);

  return keys.size === 1 ? [...keys][0] : null;
};

/**
 * The series carrying a report, one per group present among the candidates.
 *
 * For the writer, which downloads whole studies and has no series on screen to
 * resolve against. Callers hand over the series they actually download — already
 * region-filtered, when the run has a filter — so the groups found here are exactly
 * the groups the run covers.
 *
 * Delegates to findReportSeriesOfGroup rather than repeating it, on purpose: the
 * plural and the singular have to land on the same series inside a group, or the
 * writer would fill a file the reader does not open. Made structural here, so the
 * two cannot drift apart on a later edit.
 *
 * @param {Object[]} candidates Normalised series, as built by the adapters
 * @returns {Object[]} One candidate per group that has a carrying series
 */
export const findReportSeriesPerGroup = candidates =>
  [...groupKeysOf(candidates || [])]
    .map(groupKey => findReportSeriesOfGroup(candidates, groupKey))
    .filter(Boolean);

/**
 * The candidate the viewer has on screen, matched on the display set id the active
 * viewport itself carries — the same handle the thumbnails and the viewer commands
 * use to name what is displayed.
 *
 * Answers undefined both for "no viewport filled yet" and for a display set id that
 * is not among these candidates, the latter happening for a render or two while a
 * study round trip settles. Neither is an absence of report, and the caller has to
 * keep them apart from one.
 *
 * @param {Object[]} candidates Normalised series, as built by fromDisplaySets
 * @param {string} [displaySetInstanceUID] The active viewport's display set
 * @returns {Object|undefined}
 */
export const findShownCandidate = (candidates, displaySetInstanceUID) =>
  displaySetInstanceUID
    ? (candidates || []).find(
        candidate => candidate.displaySetInstanceUID === displaySetInstanceUID
      )
    : undefined;

/**
 * Adapts the viewer's display sets, whose series fields are flat. They carry no
 * Orthanc id: the viewer only ever sees DICOM UIDs. The display set id travels
 * along, because it is how the viewport names the series it shows.
 * @param {Object[]} displaySets
 * @returns {Object[]} Normalised candidates
 */
export const fromDisplaySets = displaySets =>
  (displaySets || []).map(displaySet => ({
    SeriesDescription: displaySet.SeriesDescription,
    SeriesNumber: displaySet.SeriesNumber,
    SeriesInstanceUID: displaySet.SeriesInstanceUID,
    displaySetInstanceUID: displaySet.displaySetInstanceUID,
  }));

/**
 * Adapts the series of an Orthanc inventory, whose fields sit under
 * MainDicomTags and which do carry the internal id. No display set id: display sets
 * are a viewer notion, and Orthanc knows nothing of them.
 * @param {Object[]} series Entries of /series?expand
 * @returns {Object[]} Normalised candidates
 */
export const fromInventorySeries = series =>
  (series || []).map(entry => {
    const tags = entry.MainDicomTags || {};

    return {
      SeriesDescription: tags.SeriesDescription,
      SeriesNumber: tags.SeriesNumber,
      SeriesInstanceUID: tags.SeriesInstanceUID,
      orthancId: entry.ID,
    };
  });
